import { useCallback, useMemo } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Select } from 'foundations-components/transitional/components/Select';
import Skeleton from 'personalization-lib/components/shared/display/Skeleton';
import ManageVariantsLink from 'personalization-lib/components/shared/links/ManageVariantsLink';
import VariantSelectValue from 'personalization-lib/components/variantSelect/VariantSelectValue';
import VariantSelectOption from 'personalization-lib/components/variantSelect/VariantSelectOption';
import VariantSelectFooter from 'personalization-lib/components/variantSelect/VariantSelectFooter';
import VariantSelectErrorState from 'personalization-lib/components/variantSelect/VariantSelectErrorState';
import UpgradePointButton from 'personalization-lib/components/shared/upgrades/UpgradePointButton';
import withInlineErrorHandling from 'personalization-lib/components/shared/hocs/withInlineErrorHandling';
import withPersonalizationAccess from 'personalization-lib/components/shared/hocs/withPersonalizationAccess';
import useVariants from 'personalization-lib/hooks/variants/useVariants';
import useButtonClickTracker from 'personalization-lib/hooks/tracking/library/useButtonClickTracker';
import { getFeatureGates, hasAssetPersonalizationScope } from 'personalization-lib/utils/auth';
import { getVariantName } from 'personalization-lib/utils/variantSelect';
import { getIsInIframe } from 'personalization-lib/utils/location';
import { jsx as _jsx } from "react/jsx-runtime";
const VariantSelect = ({
  value,
  strategyId,
  assetType,
  upgradeKey,
  onChange,
  onError,
  'data-onboarding-target': onboardingTarget,
  actionSource,
  showActions = true,
  selectProps
}) => {
  const isInIFrame = getIsInIframe();
  const {
    variants,
    loading,
    error
  } = useVariants(strategyId, onError);
  const hasInContextVariantCreation = getFeatureGates().createVariantInEditor;
  const trackButtonClick = useButtonClickTracker({
    actionSource
  });
  const valueNotFound = useMemo(() => {
    var _variants$find;
    return value && !((_variants$find = variants.find(variant => variant.assetCoordinates.id === value)) !== null && _variants$find !== void 0 && _variants$find.assetData);
  }, [variants, value]);
  const options = useMemo(() => variants.filter(variant => variant.assetData).map(variant => Object.assign({
    value: variant.assetCoordinates.id,
    text: getVariantName(variant.assetData),
    loading,
    error
  }, variant)), [variants, loading, error]);
  const handleChange = useCallback(e => {
    trackButtonClick.variantSelect();
    onChange(e.target.value);
  }, [onChange, trackButtonClick]);
  if (assetType && !hasAssetPersonalizationScope(assetType)) {
    return upgradeKey && /*#__PURE__*/_jsx(UpgradePointButton, {
      upgradeKey: upgradeKey,
      "data-test-id": "variant-select-upgrade",
      use: "tertiary-light",
      size: "small",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "personalizationLib.variantSelect.placeholder"
      })
    });
  }
  if (isInIFrame) {
    return /*#__PURE__*/_jsx(ManageVariantsLink, {
      strategyId: strategyId
    });
  }
  if (loading) {
    return /*#__PURE__*/_jsx(Select, Object.assign({
      "data-test-id": "variant-select",
      minInlineSize: "200px",
      menuWidth: 400,
      buttonUse: "tertiary-light",
      buttonSize: "small",
      disabled: true,
      valueRenderer: () => /*#__PURE__*/_jsx(Skeleton, {
        blockSize: "1em",
        borderRadius: "400",
        use: "on-dark"
      })
    }, selectProps));
  }
  if (valueNotFound) {
    return /*#__PURE__*/_jsx(VariantSelectErrorState, {});
  }
  return /*#__PURE__*/_jsx(Select, Object.assign({
    "data-test-id": "variant-select",
    "data-onboarding-target": onboardingTarget !== null && onboardingTarget !== void 0 ? onboardingTarget : 'variant-select',
    buttonUse: "tertiary-light",
    buttonSize: "small",
    menuWidth: 400,
    maxInlineSize: "280px",
    value: value,
    valueComponent: VariantSelectValue,
    error: Boolean(error),
    disabled: Boolean(error),
    dropdownFooter: showActions && (hasInContextVariantCreation ? /*#__PURE__*/_jsx(VariantSelectFooter, {
      strategyId: strategyId,
      defaultAsset: variants.at(-1)
    }) : /*#__PURE__*/_jsx(ManageVariantsLink, {
      strategyId: strategyId
    })),
    placeholder: I18n.text('personalizationLib.variantSelect.placeholder'),
    onChange: handleChange,
    optionComponent: VariantSelectOption,
    options: options
  }, selectProps));
};
export default withInlineErrorHandling(withPersonalizationAccess(VariantSelect), {
  errorFallback: /*#__PURE__*/_jsx(VariantSelectErrorState, {})
});